import React from 'react'
import dayjs from 'dayjs'
import { LocationDetail } from '@core/Areas/VenueMode/Hooks/useVenueProximity'
import { isReactNativeApp, sendToNative } from '@mobi/web-native-comms/web'
import { useGeoLocationPermission } from '@core/Areas/VenueMode/Hooks/useGeoLocationPermission'
import { getPointDistance } from '@core/Areas/VenueMode/Utils/geoFenceUtils'
import {
  Overview,
  Statistics,
  StatisticsRow,
  LocalButton,
  Table,
  Row,
  CellIndex,
  CellLatLon,
  CellAccuracy,
  CellDelta,
  CellMocked,
  CellPreciseAccuracy,
  CellTime,
  CellInside,
  CellDistance,
  CellVenueLocation,
  CellVenueName,
  CellError,
} from './VenueModeDiagnostics.styles'

interface VenueSettingsProps {
  locationDetail: LocationDetail
}

const MAX_VISIBLE_LOCATION_UPDATES = 100
const MAX_LOCATION_UPDATES = 1000

export const VenueModeDiagnostics: React.FC<VenueSettingsProps> = ({ locationDetail }) => {
  const [lastKnownLocations, setLastKnownLocations] = React.useState<LocationDetail[]>([])
  const [numberOfUpdates, setNumberOfUpdates] = React.useState(0)
  const [numberOfErrors, setNumberOfErrors] = React.useState(0)
  const [numberOfInside, setNumberOfInside] = React.useState(0)

  const geoLocationPermission = useGeoLocationPermission()

  React.useEffect(() => {
    if (!locationDetail?.geoLocation) return

    setLastKnownLocations(prev => {
      const prevLocation = prev[0]
      let delta = 0
      if (
        locationDetail.geoLocation?.latitude &&
        locationDetail.geoLocation.longitude &&
        prevLocation?.geoLocation?.latitude &&
        prevLocation.geoLocation.longitude
      ) {
        delta = getPointDistance(
          { lat: locationDetail.geoLocation.latitude, lon: locationDetail.geoLocation.longitude },
          { lat: prevLocation.geoLocation.latitude, lon: prevLocation.geoLocation.longitude }
        )
        locationDetail.delta = Math.round(delta)
      }

      return [locationDetail, ...prev].slice(0, MAX_LOCATION_UPDATES)
    })
    setNumberOfUpdates(prev => ++prev)
    if (locationDetail.error) setNumberOfErrors(prev => ++prev)
    if (locationDetail.closestVenue?.inside) setNumberOfInside(prev => ++prev)
  }, [
    locationDetail?.geoLocation?.latitude,
    locationDetail?.geoLocation?.longitude,
    locationDetail?.geoLocation?.timestamp,
    locationDetail?.error,
    locationDetail?.closestVenue?.inside,
  ])

  const displayableLocations = lastKnownLocations.slice(0, MAX_VISIBLE_LOCATION_UPDATES)
  const shownCount = Math.min(lastKnownLocations.length, MAX_VISIBLE_LOCATION_UPDATES)

  const exportData = () => {
    const defaultFilename = `venue_mode_diagnostics_${dayjs().format('YYYYMMDD_HHmmss')}.json`
    const outputFileName = prompt('Downloading\n\n\nSave as... or cancel saving', defaultFilename)
    if (outputFileName === null) return

    try {
      const data = { lastKnownLocations }
      const dataJson = JSON.stringify(
        data,
        (key, value) => (key == '_persist' ? undefined : value), // remove the redux persist info
        2
      )
      if (isReactNativeApp) {
        sendToNative('SHARE_FILE_REQUEST', { data: dataJson, file: outputFileName })
      } else {
        const blob = new Blob([dataJson], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = outputFileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    } catch (error) {
      alert(`Error exporting data\n\n${error}`)
    }
  }

  const clearData = () => {
    if (!confirm(`Are you sure you want to clear the page's data?`)) return
    setLastKnownLocations([])
  }

  return (
    <div>
      <Overview>
        <Statistics>
          <StatisticsRow>
            Shown: {shownCount}, Total: {numberOfUpdates}, Errors: {numberOfErrors}, Inside:{' '}
            {numberOfInside}
          </StatisticsRow>
          {!isReactNativeApp && (
            <StatisticsRow>Web Permission 'Access Location': {geoLocationPermission}</StatisticsRow>
          )}
          {isReactNativeApp && (
            <StatisticsRow>
              App Permission 'Precision Accuracy':{' '}
              {locationDetail.geoLocation?.isPreciseAccuracy?.toString() ?? '-'}
            </StatisticsRow>
          )}
        </Statistics>
        <LocalButton type='button' onClick={exportData}>
          Export
        </LocalButton>
        <LocalButton type='button' onClick={clearData}>
          Clear
        </LocalButton>
      </Overview>

      <Table>
        <Row backgroundShade='dark'>
          <CellIndex>#</CellIndex>
          <CellLatLon>Latitude</CellLatLon>
          <CellLatLon>Longitude</CellLatLon>
          <CellAccuracy>Accuracy</CellAccuracy>
          <CellDelta>∆</CellDelta>
          <CellMocked>Mocked</CellMocked>
          <CellPreciseAccuracy>Precise</CellPreciseAccuracy>
          <CellTime>Timestamp</CellTime>
          <CellInside>Inside</CellInside>
          <CellDistance>Distance</CellDistance>
          <CellVenueLocation>VL</CellVenueLocation>
          <CellVenueName>Name</CellVenueName>
          <CellError>Error</CellError>
        </Row>
        {displayableLocations.map(({ geoLocation, closestVenue, error, delta }, index) => {
          return (
            <Row
              key={index}
              backgroundShade={
                error
                  ? 'error'
                  : closestVenue?.inside
                    ? 'inside'
                    : index % 2 == 0
                      ? 'light'
                      : 'dark'
              }
              onClick={() => error && alert(`Error for this record:\n\n${error}`)}
            >
              <CellIndex>{index + 1}</CellIndex>
              <CellLatLon>{geoLocation?.latitude}</CellLatLon>
              <CellLatLon>{geoLocation?.longitude}</CellLatLon>
              <CellAccuracy>{prettifyDistance(geoLocation?.accuracy)}</CellAccuracy>
              <CellDelta> {delta ? prettifyDistance(delta) : '-'} </CellDelta>
              <CellMocked>{geoLocation?.mocked?.toString() ?? '-'}</CellMocked>
              <CellPreciseAccuracy>
                {geoLocation?.isPreciseAccuracy?.toString() ?? '-'}
              </CellPreciseAccuracy>
              <CellTime>
                {dayjs(geoLocation?.timestamp).tz('Australia/Perth').format('HH:mm:ss.SSS')}
              </CellTime>
              <CellInside>{closestVenue?.inside ? 'yes' : 'no'}</CellInside>
              <CellDistance>{prettifyDistance(closestVenue?.distance)}</CellDistance>
              <CellVenueLocation>
                {closestVenue?.venueId}
                {closestVenue?.locationId}
              </CellVenueLocation>
              <CellVenueName>{closestVenue?.name}</CellVenueName>
              <CellError>{error ?? '-'}</CellError>
            </Row>
          )
        })}
      </Table>
    </div>
  )
}

const prettifyDistance = (dist?: number | null) => {
  if (dist == undefined) return '0m'
  if (dist < 1000) return `${Math.round(dist) != dist ? dist.toFixed(3) : dist.toFixed(1)}m`
  const kilometres = dist / 1000
  if (kilometres < 10) return `${kilometres.toFixed(2)}km`
  return `${Math.round(kilometres).toLocaleString('en-AU')}km`
}
